import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Send, Sparkles, User, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAiSidebar } from '@/store/AiSidebarContext'
import { useAppStore } from '@/store/AppContext'
import { useAiChat } from '@/hooks/useAiChat'
import type { Camp } from '@/types/database'
import type { CampData } from '@/store/reducer'

const SUGGESTION_KEYS = [
  'misc.aiSidebarPanel.suggestionItinerary',
  'misc.aiSidebarPanel.suggestionBudget',
  'misc.aiSidebarPanel.suggestionTasks',
]

const emptyCampData: CampData = {
  itineraryDays: [],
  activities: [],
  quotes: [],
  quoteItems: [],
  paymentMilestones: [],
  participants: [],
  tasks: [],
  notes: [],
  campPartners: [],
  expenseActuals: [],
  expenseEstimates: [],
}

/** Extract campId from URL paths like /camps/:id or /tours/:id */
function extractCampIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/(?:trips|tours|camps)\/([^/]+)/)
  return match?.[1] ?? null
}

export function AiSidebarPanel() {
  const { t } = useTranslation()
  const { isOpen, close, activeCampId } = useAiSidebar()
  const { state } = useAppStore()
  const location = useLocation()

  // Auto-detect trip from URL or fallback to context
  const resolvedCampId = useMemo(() => {
    return extractCampIdFromPath(location.pathname) ?? activeCampId
  }, [location.pathname, activeCampId])

  const camp = useMemo(
    () => (resolvedCampId ? state.camps.find((c: Camp) => c.id === resolvedCampId) ?? null : null),
    [state.camps, resolvedCampId],
  )

  const campData = useMemo(
    () => (resolvedCampId ? state.campData[resolvedCampId] ?? emptyCampData : emptyCampData),
    [state.campData, resolvedCampId],
  )

  if (!isOpen) return null

  return (
    <>
      {/* Mobile backdrop — tap to dismiss the overlay drawer */}
      <div
        className="fixed inset-0 z-40 bg-black/40 md:hidden"
        onClick={close}
        aria-hidden
      />
      {/* Inline panel on desktop; right-side overlay drawer on mobile. */}
      <aside
        className={cn(
          'flex flex-col bg-surface border-l border-border',
          'fixed inset-y-0 right-0 z-50 w-[88vw] max-w-[360px] shadow-xl',
          'md:static md:z-auto md:h-full md:w-[350px] md:max-w-none md:shrink-0 md:shadow-none',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 h-14 px-4 shrink-0 border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="size-4 shrink-0 text-ai" />
            <span className="truncate text-sm font-semibold text-text">{t('misc.aiSidebarPanel.title')}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={close}
            className="shrink-0 size-9 p-0 text-text-2 hover:text-text"
            aria-label={t('misc.aiSidebarPanel.close')}
          >
            <X className="size-4" />
          </Button>
        </div>

        {camp && campData ? (
          <AiSidebarChat camp={camp} campData={campData} />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
            <div className="flex size-10 items-center justify-center rounded-full bg-surface-2">
              <Sparkles className="size-5 text-text-3" />
            </div>
            <p className="text-sm text-text-2">{t('misc.aiSidebarPanel.selectCamp')}</p>
            <p className="text-xs text-text-3">{t('misc.aiSidebarPanel.emptyHintBefore')}<br />{t('misc.aiSidebarPanel.emptyHintAfter')}</p>
          </div>
        )}
      </aside>
    </>
  )
}

function AiSidebarChat({ camp, campData }: { camp: Camp; campData: CampData }) {
  const { t } = useTranslation()
  const { messages, isLoading, error, sendMessage } = useAiChat({ camp, campData })
  const [input, setInput] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const scrollToBottom = useCallback(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, isLoading, scrollToBottom])

  const handleSend = (text: string) => {
    if (!text.trim() || isLoading) return
    sendMessage(text.trim())
    setInput('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    handleSend(input)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      handleSend(input)
    }
  }

  return (
    <>
      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 p-4">
        {messages.length === 0 && !isLoading && (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <div className="flex size-10 items-center justify-center rounded-full bg-ai/10">
              <Sparkles className="size-5 text-ai" />
            </div>
            <div className="text-center">
              <p className="text-sm text-text-3">
                {t('misc.aiSidebarPanel.prompt')}
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2 justify-center">
              {SUGGESTION_KEYS.map(key => {
                const label = t(key)
                return (
                  <button
                    type="button"
                    key={key}
                    onClick={() => handleSend(label)}
                    className="rounded-full border border-border bg-surface-2 px-2.5 py-1 text-xs text-text-2 outline-none transition-colors hover:bg-surface-3 hover:text-text focus-visible:ring-2 focus-visible:ring-accent/60"
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={cn('flex gap-2', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
            {msg.role === 'assistant' && (
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-ai/10 mt-0.5">
                <Sparkles className="size-3 text-ai" />
              </div>
            )}
            <div className={cn(
              'max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap',
              msg.role === 'user' ? 'bg-accent text-white' : 'bg-surface-2 text-text'
            )}>
              {msg.content}
            </div>
            {msg.role === 'user' && (
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-surface-3 mt-0.5">
                <User className="size-3 text-text-3" />
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex gap-2">
            <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-ai/10 mt-0.5">
              <Sparkles className="size-3 text-ai" />
            </div>
            <div className="max-w-[85%] rounded-xl bg-surface-2 px-3 py-2 text-sm leading-relaxed text-text whitespace-pre-wrap">
              <Loader2 className="size-4 animate-spin text-text-3" />
            </div>
          </div>
        )}

        {error && (
          <div className="flex gap-2">
            <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-ai/10 mt-0.5">
              <Sparkles className="size-3 text-ai" />
            </div>
            <div className="max-w-[85%] rounded-xl bg-surface-2 px-3 py-2 text-sm leading-relaxed text-text whitespace-pre-wrap">
              {error}
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="shrink-0 border-t border-border p-3">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('misc.aiSidebarPanel.inputPlaceholder')}
            aria-label={t('misc.aiSidebarPanel.inputLabel')}
            rows={1}
            className="flex-1 resize-none rounded-lg border border-border bg-surface-2 px-3 py-2 text-base text-text placeholder:text-text-3 outline-none transition-[border-color,box-shadow] focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/40 md:text-sm"
          />
          <Button type="submit" size="sm" disabled={!input.trim() || isLoading} className="shrink-0 bg-accent hover:bg-accent/90">
            <Send className="size-4" />
          </Button>
        </div>
      </form>
    </>
  )
}
